import { types as api } from '@mesa-labs/mesa-api';
import { LoadingSpinner } from '@mesa-labs/mesa-ui';
import React, { useState } from 'react';

import { useGetVendorActionItemsQuery } from '../../../../redux/api/vendors';
import { relatedActionItems } from '../../../../utils/vendorActionItems';
import ActionItemsTable from './ActionItemsTable';
import {
  ErrorText, LinkButton, Muted, SectionHeader, SectionHeaderStart, SectionLabel,
} from './styles';

const NoItems: readonly api.VendorActionItemResponse[] = [];

type VendorActionItemsSectionProps = {
  vendorId: string;
  // Left out of the list: the item being viewed.
  excludeId?: string;
  label: string;
  me?: string;
  canWrite: boolean;
  onOpen: (item: api.VendorActionItemResponse) => void;
  // Right of the heading, e.g. "+ New".
  action?: React.ReactNode;
  // Between the heading and the table, e.g. the inline create form.
  children?: React.ReactNode;
};

// The Vendor's open Action Items, newest first, with a "Show closed" toggle that adds the closed ones after them. Shared
// by the detail drawer (the item's related items) and the create form (to spot a duplicate before creating).
export default function VendorActionItemsSection({
  vendorId, excludeId, label, me, canWrite, onOpen, action, children,
}: VendorActionItemsSectionProps): React.ReactElement {
  const [showClosed, setShowClosed] = useState(false);
  const { data: items = NoItems, isLoading, isError } = useGetVendorActionItemsQuery({ vendorId });
  const { open, closed } = relatedActionItems(items, excludeId);
  // Closed items join the rows when shown; the table lists them after the open ones.
  const rows = showClosed ? [...open, ...closed] : open;
  return (
    <div>
      <SectionHeader>
        <SectionHeaderStart>
          <SectionLabel>{`${label}${open.length > 0 ? ` (${open.length})` : ''}`}</SectionLabel>
          {closed.length > 0 && (
            <LinkButton type="button" onClick={() => setShowClosed(!showClosed)}>{`${showClosed ? 'Hide' : 'Show'} closed (${closed.length})`}</LinkButton>
          )}
        </SectionHeaderStart>
        {action}
      </SectionHeader>
      {children}
      {isError && <ErrorText role="alert">There was an error loading the Vendor&apos;s action items</ErrorText>}
      {isLoading && <LoadingSpinner height="18px" />}
      {!isLoading && !isError && rows.length === 0 && <Muted>{excludeId ? 'No other open action items' : 'No open action items'}</Muted>}
      {!isLoading && <ActionItemsTable items={rows} me={me} canWrite={canWrite} density="compact" onOpen={onOpen} />}
    </div>
  );
}
